import { useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'
import { Provider } from 'react-redux'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from './shared/components/ui/sonner'
import { ThemeProvider } from './shared/components/ThemeProvider'
import { AuthProvider } from './features/auth/components/AuthProvider'
import { ErrorBoundary } from './shared/components/ErrorBoundary'
import { DrawerProvider } from './layouts/DrawerContext'
import { store } from './shared/store'
import { router } from './router'
import { OfflineBanner } from './features/sync/components/OfflineBanner'
import { useSyncQueueDrain } from './features/sync/hooks/useSyncQueueDrain'
import { queryClient } from './shared/lib/queryClient'

/**
 * Mounted once inside the Redux provider: drains the sync queue on app
 * start and on an interval, so items queued over a dead link (where the
 * window 'online' event never fires) still sync.
 */
function SyncQueueDrain() {
  useSyncQueueDrain()
  return null
}

/**
 * App root component
 * - Sets up all providers (Redux, React Query, Theme, Auth, Drawer)
 * - Wraps app in ErrorBoundary
 * - Provides router configuration
 * - Prevents refresh during offline mode
 * - Tracks offline refresh attempts for recovery
 */
function App() {
  useEffect(() => {
    // Prevent refresh during offline mode
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!navigator.onLine) {
        // Set flag to detect forced refresh
        localStorage.setItem('offlineRefreshAttempted', Date.now().toString())
        e.preventDefault()
        const message = 'You are offline. Refreshing may lose unsaved data.'
        e.returnValue = message
        return message
      }
      // Clear flag when online
      localStorage.removeItem('offlineRefreshAttempted')
      return undefined
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider defaultTheme="system" storageKey="arborlist-theme">
            <AuthProvider>
              <DrawerProvider>
                <SyncQueueDrain />
                <OfflineBanner />
                <RouterProvider router={router} />
                <Toaster />
              </DrawerProvider>
            </AuthProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  )
}

export default App
